import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AISummaryLoader } from '../../components/onboarding';
import {
  Button,
  MarkdownRenderer,
  Spinner,
  Textarea,
  Toaster,
} from '../../components/common';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { routes } from '../../lib/routes';
import {
  Edit,
  FloppyDisk,
  HexagonDice,
  HomeSimple,
  NavArrowDown,
  NavArrowUp,
  PageEdit,
  UnionHorizAlt,
  Xmark,
} from 'iconoir-react';
import toast from 'react-hot-toast';
import { useAiSummary, useUpdateAiSummary } from '../../hooks/useProfile.ts';
import { useApproveAISummary } from '../../hooks/useOnboarding.ts';
import { useSocketEvent } from '../../hooks/useSocketEvent.ts';
import { api } from '../../utils';

/**
 * Section parsed from a persona markdown narrative (## headers).
 */
interface Section {
  title: string;
  content: string;
  index: number;
}

/**
 * Parse a persona markdown narrative (## headers) into sections.
 * Mirrors mobile persona-review.tsx parseSections (May-01 commit 4a54492).
 * Skips any preamble before the first ## header (typically "# a user"
 * H1 title — kept separately as h1Title).
 */
function parseSections(markdown: string): Section[] {
  if (!markdown) return [];
  const stripped = markdown.replace(/^#\s+[^\n]+\n+/m, '');
  const parts = stripped.split(/^##\s+/m);
  const sections: Section[] = [];
  parts.slice(1).forEach((block, idx) => {
    const lines = block.split('\n');
    const title = (lines[0] || '').trim();
    const content = lines.slice(1).join('\n').trim();
    if (title) {
      sections.push({ title, content, index: idx });
    }
  });
  return sections;
}

/**
 * Extract the H1 title (e.g., "# The Voice AI Founder Raising Her Series A")
 * from the original markdown so it can render above the section list.
 */
function extractH1(markdown: string): string {
  if (!markdown) return '';
  const m = markdown.match(/^#\s+([^\n]+)/m);
  return m ? m[1].trim() : '';
}

/**
 * Rebuild the full markdown from sections after one was edited.
 * Preserves the original order + the h1 title prefix if present.
 * Mirrors mobile persona-review.tsx rebuildMarkdown.
 */
function rebuildMarkdown(originalMarkdown: string, sections: Section[]): string {
  const h1Match = originalMarkdown.match(/^(#\s+[^\n]+\n+)/m);
  const h1 = h1Match ? h1Match[1] : '';
  const body = sections
    .map(s => `## ${s.title}\n${s.content}`)
    .join('\n\n\n');
  return `${h1}${body}\n`;
}

/**
 * Truncate a section's content for the collapsed preview state.
 * Mobile renders ~80 chars + "tap to read more"; web has more horizontal
 * room so we use ~280 chars before truncating.
 */
function truncatePreview(content: string, maxChars = 280): { preview: string; truncated: boolean } {
  const flat = content.replace(/\s+/g, ' ').trim();
  if (flat.length <= maxChars) return { preview: flat, truncated: false };
  // May-06 fix: respect word boundaries — backtrack to last whitespace before
  // maxChars to avoid mid-word cuts ("trust the found...", "real-time c...").
  // Guard: if no whitespace in the last 30% of the slice, fall back to hard cut
  // (handles edge case of a single very-long token).
  const sliced = flat.slice(0, maxChars);
  const lastSpace = sliced.lastIndexOf(' ');
  const cleanCut =
    lastSpace > maxChars * 0.7 ? sliced.slice(0, lastSpace) : sliced;
  return { preview: cleanCut.trimEnd(), truncated: true };
}

/**
 * Heuristic for pipe-separated tag content (e.g., the Focus section).
 * If a section's content contains 3+ ` | ` separators AND each segment is
 * reasonably short (< 60 chars), it's a tag list — render as chips for
 * scannability instead of inline pipes.
 */
function detectTagList(content: string): string[] | null {
  if (!content || !content.includes(' | ')) return null;
  const parts = content
    .split(' | ')
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length < 3) return null;
  if (parts.some(p => p.length > 60)) return null;
  return parts;
}

/**
 * Jun-17 (Phase 2a, V1-structured-persona scope): the persona sections that
 * actually drive matching. We give them a clearer B2B display label and an
 * explicit "Used to match you" tag so the review reads as a CONFIRM-what-we-
 * captured step (mirroring V2's structured review) rather than a passive read.
 * The underlying section title is kept unchanged for editing/save — handlers
 * key on the real title, so this is display-only and cannot reorder/rename the
 * stored persona.
 */
const MATCH_CRITICAL_TITLES = new Set(['Looking For', 'Offerings', 'Focus']);
const SECTION_DISPLAY_LABELS: Record<string, string> = {
  'Looking For': 'Who you want to meet',
  Offerings: 'What you bring',
  Focus: 'Your focus areas',
};
const sectionLabel = (title: string): string =>
  SECTION_DISPLAY_LABELS[title] || title;

const OnboardingAISummaryPage = () => {
  const navigate = useNavigate();
  const { user, checkStatus } = useAuth();

  const { data, isLoading, refetch } = useAiSummary();
  const { mutateAsync: approveAISummary, isPending: isApproving } =
    useApproveAISummary();
  const { mutateAsync: updateSummary, isPending: isUpdatingSummary } =
    useUpdateAiSummary();

  // Per-section editing state (May-05 rewrite per m3-proposal-preview.html A2 spec).
  // Replaces the pre-May-05 single-textarea-edits-whole-markdown flow with
  // tap-to-edit per section + "Read more" expand on truncated previews.
  // Mirrors mobile persona-review.tsx (May-01 commit 4a54492).
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [expandedTitles, setExpandedTitles] = useState<Set<string>>(new Set());

  const summaryText: string = data?.result?.summary || '';
  const summaryId: string = data?.result?.id || '';
  const h1Title = useMemo(() => extractH1(summaryText), [summaryText]);
  const sections = useMemo(() => parseSections(summaryText), [summaryText]);

  const handleStartEdit = (section: Section) => {
    setEditingTitle(section.title);
    setEditDraft(section.content);
  };

  const handleCancelEdit = () => {
    setEditingTitle(null);
    setEditDraft('');
  };

  const handleSaveEdit = async () => {
    if (!editingTitle || !summaryId) return;
    const newSections = sections.map(s =>
      s.title === editingTitle ? { ...s, content: editDraft.trim() } : s,
    );
    const newMarkdown = rebuildMarkdown(summaryText, newSections);
    try {
      await updateSummary(
        {
          id: summaryId,
          summary: newMarkdown,
        },
        {
          onSuccess: () => {
            toast.custom(t => (
              <Toaster
                message={`"${editingTitle}" updated: matches will use the edited version`}
                type='success'
                id={t.id}
              />
            ));
            setEditingTitle(null);
            setEditDraft('');
            refetch();
          },
          onError: (error: any) => {
            toast.custom(t => (
              <Toaster
                message={error?.message || 'Failed to update section'}
                type='error'
                id={t.id}
              />
            ));
          },
        },
      );
    } catch (err) {
      console.error('[OnboardingAISummaryPage] save section failed:', err);
    }
  };

  const toggleExpand = (title: string) => {
    setExpandedTitles(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  useSocketEvent('summary.ready', () => {
    window.scrollTo(0, 0);
    refetch();
  });

  const handleApproveAISummary = async () => {
    await checkStatus();
    api
      .post('/analytics/track', {
        event_type: 'onboarding_complete',
        event_category: 'onboarding',
        event_data: { source: 'web' },
      })
      .catch(() => {});

    await approveAISummary(summaryId, {
      onSuccess: () => {
        navigate(routes.home);
      },
      onError: error => {
        toast.custom(t => (
          <Toaster message={error?.message as string} type='error' id={t.id} />
        ));
      },
    });
  };

  if ((!data?.result || !data?.result?.webhook) && !isLoading) {
    return <AISummaryLoader />;
  }

  if (isLoading) {
    return (
      <div className='h-[calc(100vh-6rem)] overflow-y-auto mt-2'>
        <div className='container px-4 py-6 md:px-8 md:pt-[3.5625rem] md:pb-[6.5rem] flex items-center justify-center'>
          <Spinner />
        </div>
      </div>
    );
  }

  // Empty-summary fallback (webhook completed but no content generated)
  if (data?.result && data?.result?.webhook && !data?.result?.summary) {
    return (
      <div className='h-[calc(100vh-6rem)] overflow-y-auto mt-2'>
        <div className='container px-4 py-6 md:px-8 md:pt-[3.5625rem] md:pb-[6.5rem]'>
          <h2 className='mb-1 font-heading !font-medium text-heading-500 flex items-center gap-2'>
            <HexagonDice className='w-12 h-12 object-contain' />
            AI Summary
          </h2>
          <p className='mb-10 text-body-400'>
            No AI summary available yet. Your profile will be analyzed shortly.
          </p>
        </div>
        <div className='container px-4 md:px-8 pb-[6.5rem]'>
          <div className='sm:flex justify-end gap-2'>
            <Button
              className='flex items-center gap-2 sm:mb-0 mb-2 sm:w-fit w-full'
              variant='outline'
              onClick={() => {
                navigate(routes.onboardingSummary);
              }}
            >
              <PageEdit className='w-6 h-6' />
              Update responses
            </Button>
            <Button
              className='flex items-center gap-2 sm:w-fit w-full'
              color='dark'
              onClick={handleApproveAISummary}
              isLoading={isApproving}
            >
              {user?.onboarding_matches ? (
                <>
                  <HomeSimple className='w-6 h-6' />
                  Go to dashboard
                </>
              ) : (
                <>
                  <UnionHorizAlt className='w-6 h-6' />
                  Find matches
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const ActionBar = () => (
    <div className='sm:flex justify-end gap-2'>
      <Button
        className='flex items-center gap-2 sm:mb-0 mb-2 sm:w-fit w-full'
        variant='outline'
        onClick={() => navigate(routes.onboardingSummary)}
      >
        <PageEdit className='w-6 h-6' />
        Update responses
      </Button>
      <Button
        className='flex items-center gap-2 sm:w-fit w-full'
        color='dark'
        onClick={handleApproveAISummary}
        isLoading={isApproving}
      >
        {user?.onboarding_matches ? (
          <>
            <HomeSimple className='w-6 h-6' />
            Go to dashboard
          </>
        ) : (
          <>
            <UnionHorizAlt className='w-6 h-6' />
            Find matches
          </>
        )}
      </Button>
    </div>
  );

  return (
    <div className='h-[calc(100vh-6rem)] overflow-y-auto mt-2'>
      <div className='container px-4 py-6 md:px-8 md:pt-[3.5625rem] md:pb-[6.5rem]'>
        {/* May-06 UI polish: header restructured for visual hierarchy.
             - "AI Summary" demoted from h2 to compact label-style heading
               (it's the page chrome, not the content; the archetype is
               the content)
             - Archetype promoted from h3 text-lg to display-style heading
               with primary color accent. This is what the user actually
               cares about: how the system has positioned them.
             - Subtitle anchored below archetype so the read order is
               page-label -> archetype -> subtitle -> action bar -> sections.
             - Top action bar moved into a card-like header surface so it
               doesn't look stranded between subtitle and first section. */}
        <div className='mb-8 bg-gradient-to-br from-primary-50 to-white border border-primary-100 rounded-2xl p-6 md:p-8'>
          <div className='flex items-center gap-2 mb-3 text-sm text-primary-600 font-medium uppercase tracking-wide'>
            <HexagonDice className='w-5 h-5' />
            AI Summary
          </div>
          {h1Title && (
            <h1 className='font-heading !font-medium text-heading-600 text-2xl md:text-3xl leading-tight mb-3'>
              {h1Title}
            </h1>
          )}
          <p className='text-body-400 text-sm leading-relaxed mb-5 max-w-2xl'>
            This is exactly what we'll match you on. Review it and confirm: the items tagged "Used to match you" matter most. Tap any section to edit (changes save immediately and matches refresh), then Find matches when it looks right. This profile, and the AI's analysis of why you match, are shown to the people you connect with, so make it read the way you'd want a new connection to see you.
          </p>
          <ActionBar />
        </div>

        {/* Section list — May-05 rewrite. Per-section tap-to-edit with truncated
             preview + "Read more" expand. Mirrors mobile persona-review.tsx
             pattern (May-01 commit 4a54492).
             May-06 polish: gap-3 -> gap-5 for better card breathing room. */}
        <div className='flex flex-col gap-5'>
          {sections.length === 0 && summaryText && (
            // Defensive: persona without ## headers (legacy shape) — fall back
            // to whole-markdown render so the user still sees content.
            <div className='bg-white border border-border-100 rounded-2xl p-4'>
              <MarkdownRenderer markdown={summaryText} />
            </div>
          )}

          {sections.map(section => {
            const isEditingThis = editingTitle === section.title;
            const isExpanded = expandedTitles.has(section.title);
            const { preview, truncated } = truncatePreview(section.content);

            return (
              <div
                key={section.title}
                className='bg-white border border-border-100 rounded-2xl p-4 transition hover:border-primary-300'
              >
                {isEditingThis ? (
                  <div className='flex flex-col gap-3'>
                    <div className='flex items-center justify-between'>
                      <h4 className='font-heading !font-medium text-heading-500 flex items-center gap-2 flex-wrap'>
                        {sectionLabel(section.title)}
                        {MATCH_CRITICAL_TITLES.has(section.title) && (
                          <span className='inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[0.625rem] font-medium uppercase tracking-wide'>
                            Used to match you
                          </span>
                        )}
                      </h4>
                    </div>
                    <Textarea
                      wrapperClassName=''
                      textareaWrapperClassName='!h-auto min-h-[12rem]'
                      textareaClassName='resize-none min-h-[10rem] text-sm leading-relaxed'
                      value={editDraft}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setEditDraft(e.target.value)
                      }
                      placeholder={`Edit ${section.title.toLowerCase()}...`}
                    />
                    <div className='flex flex-col sm:flex-row justify-end gap-2'>
                      <Button
                        className='w-full sm:w-auto flex items-center gap-2'
                        variant='outline'
                        onClick={handleCancelEdit}
                        disabled={isUpdatingSummary}
                      >
                        <Xmark className='w-5 h-5' />
                        Cancel
                      </Button>
                      <Button
                        color='dark'
                        className='w-full sm:w-auto flex items-center gap-2'
                        onClick={handleSaveEdit}
                        isLoading={isUpdatingSummary}
                      >
                        <FloppyDisk className='w-6 h-6' />
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className='flex items-start justify-between gap-2 mb-3'>
                      <h4 className='font-heading !font-medium text-heading-500 flex items-center gap-2 flex-wrap'>
                        {sectionLabel(section.title)}
                        {MATCH_CRITICAL_TITLES.has(section.title) && (
                          <span className='inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[0.625rem] font-medium uppercase tracking-wide'>
                            Used to match you
                          </span>
                        )}
                      </h4>
                      {/* May-06 polish: Edit button reduced to icon-only —
                          previous size='sm' outline button repeated on every
                          card created visual weight. Icon-only with hover
                          background tint stays discoverable but lighter. */}
                      <button
                        type='button'
                        onClick={() => handleStartEdit(section)}
                        className='shrink-0 p-1.5 rounded-md text-body-300 hover:text-primary-500 hover:bg-primary-50 transition'
                        title='Edit this section'
                        aria-label={`Edit ${section.title}`}
                      >
                        <Edit className='w-4 h-4' />
                      </button>
                    </div>
                    {(() => {
                      // May-06 polish: detect pipe-separated tag content
                      // (Focus section etc.) and render as chips instead of
                      // inline pipes for scannability. Heuristic: 3+ pipes,
                      // each segment < 60 chars.
                      const tags = detectTagList(section.content);
                      if (tags && (isExpanded || !truncated || section.content.length <= 280)) {
                        return (
                          <div className='flex flex-wrap gap-2'>
                            {tags.map((tag, i) => (
                              <span
                                key={`${section.title}-tag-${i}`}
                                className='inline-flex items-center px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium border border-primary-100'
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      if (isExpanded || !truncated) {
                        return (
                          <div className='text-body-400 text-sm leading-relaxed whitespace-pre-wrap'>
                            {section.content}
                          </div>
                        );
                      }
                      return (
                        <div className='text-body-400 text-sm leading-relaxed'>
                          {preview}
                          {truncated && (
                            <>
                              {'… '}
                              {/* May-06 polish: Read more gains chevron icon
                                  for affordance signal. */}
                              <button
                                type='button'
                                onClick={() => toggleExpand(section.title)}
                                className='inline-flex items-center gap-0.5 text-primary-500 hover:text-primary-600 font-medium'
                              >
                                Read more
                                <NavArrowDown className='w-3.5 h-3.5' />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })()}
                    {isExpanded && truncated && (
                      <button
                        type='button'
                        onClick={() => toggleExpand(section.title)}
                        className='mt-2 inline-flex items-center gap-0.5 text-primary-500 hover:text-primary-600 font-medium text-sm'
                      >
                        Show less
                        <NavArrowUp className='w-3.5 h-3.5' />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom action bar */}
      <div className='container px-4 md:px-8 pb-[6.5rem]'>
        <ActionBar />
      </div>
    </div>
  );
};

export default OnboardingAISummaryPage;
